// D:\directiveai-react\agthealth\agthealth-frontend\src\components\pages\appointments\AppointmentsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Switch,
  Backdrop,
  Modal
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Calendar, 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  MoreVertical,
  Edit2,
  Trash2,
  Eye,
  User,
  Clock,
  MapPin,
  Video,
  Phone,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send
} from 'lucide-react';
import { format, parseISO, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isToday, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { HEALTH_GREEN } from '../../Login/utils/mockData';
import agthealthService from '../../../services/agthealthService';

// Importar sidebar y header compartidos
import Sidebar from '../../common/sidebar/Sidebar';
import DashboardHeader from '../../common/header/DashboardHeader';
import PatientModal from '../patients/PatientModal';

const DrawerWidth = 240;

const Main = styled(Box)(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  paddingTop: 0,
  paddingBottom: theme.spacing(16), // Espacio inferior aumentado (~128px)
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isMobile ? 0 : `-${DrawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    paddingTop: 0,
    paddingBottom: theme.spacing(14), // Más espacio inferior en pantallas pequeñas (~112px)
  }
}));

const CalendarContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}));

const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  padding: theme.spacing(2)
}));

const DayHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(1),
  fontWeight: 500,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const DayCell = styled(Box)(({ theme, istoday, isselected, isoutofmonth }) => ({
  position: 'relative',
  minHeight: 100,
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: isselected === 'true' 
    ? 'rgba(76, 175, 80, 0.1)'
    : istoday === 'true'
    ? 'rgba(33, 150, 243, 0.05)'
    : 'transparent',
  opacity: isoutofmonth === 'true' ? 0.5 : 1,
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: isselected === 'true' 
      ? 'rgba(76, 175, 80, 0.2)'
      : 'rgba(0, 0, 0, 0.04)',
  }
}));

const DayNumber = styled(Box)(({ theme, istoday }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: istoday === 'true' ? HEALTH_GREEN : 'transparent',
  color: istoday === 'true' ? 'white' : theme.palette.text.primary,
  fontWeight: istoday === 'true' ? 600 : 400,
  marginBottom: theme.spacing(1)
}));

const AppointmentChip = styled(Box)(({ theme, status }) => {
  const statusColors = {
    scheduled: HEALTH_GREEN,
    completed: '#2196f3',
    cancelled: '#f44336',
    'no-show': '#ff9800'
  };
  
  const color = statusColors[status] || HEALTH_GREEN;
  
  return {
    backgroundColor: color,
    color: 'white',
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    marginRight: theme.spacing(0.5), // Añadir margen a la derecha en lugar de abajo
    marginBottom: theme.spacing(0.5),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    display: 'inline-block', // Cambiar a inline-block para alineación horizontal
    '&:hover': {
      filter: 'brightness(1.1)'
    }
  };
});

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  backgroundColor: HEALTH_GREEN,
  color: 'white',
  '&:hover': {
    backgroundColor: '#3d8b40',
  },
  fontWeight: 500,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: HEALTH_GREEN,
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: HEALTH_GREEN
  }
}));

const DetailsModal = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 500,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  boxShadow: '0 4px 30px rgba(0,0,0,0.1)',
  padding: theme.spacing(4),
  outline: 'none',
  maxHeight: '80vh',
  overflowY: 'auto'
}));

const AppointmentsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [user, setUser] = useState({
    firstName: 'Dr.',
    lastName: 'Usuario',
    specialty: 'Profesional de la salud'
  });
  
  // Estado para el modal de pacientes
  const [patientModal, setPatientModal] = useState({
    open: false,
    patientId: null
  });
  
  // Estados para el calendario
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  
  // Estados para el diálogo de citas
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [formData, setFormData] = useState({
    patientId: '',
    title: 'Consulta médica',
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    duration: 30,
    type: 'in-person',
    status: 'scheduled',
    location: '',
    notes: '',
    reason: ''
  });
  
  // Estado para el menú contextual de citas
  const [appointmentMenuAnchor, setAppointmentMenuAnchor] = useState(null);
  const [menuAppointment, setMenuAppointment] = useState(null);
  
  // Estado para el diálogo de confirmación de eliminación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  
  // Añadir estado para mensajes de éxito
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Obtener el perfil del usuario
        const userResponse = await agthealthService.getUserProfile();
        if (userResponse.data) {
          setUser(userResponse.data);
        }
        
        // Obtener las citas del mes actual
        await fetchAppointmentsForMonth(currentMonth);
        
        // Obtener todos los pacientes
        const patientsResponse = await agthealthService.getPatients();
        setPatients(patientsResponse.data || []);
        
        setError(null);
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError("No se pudieron cargar los datos");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Función para obtener las citas de un mes
  const fetchAppointmentsForMonth = async (month) => {
    try {
      const start = startOfMonth(month);
      const end = endOfMonth(month);
  
      // Convertir a ISO string manteniendo la zona horaria local
      const startDate = start.toISOString();
      const endDate = end.toISOString();
  
      // Pasar la zona horaria local al backend para que pueda hacer las conversiones correctamente
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await agthealthService.getAppointmentsByDateRange(startDate, endDate, { timezone });
  
      setAppointments(response.data || []);
    } catch (err) {
      console.error("Error al obtener citas:", err);
      throw err;
    }
  };
  
  // Función para cambiar de mes
  const changeMonth = async (direction) => {
    const newMonth = direction === 'next' 
      ? addMonths(currentMonth, 1) 
      : subMonths(currentMonth, 1);
    
    setCurrentMonth(newMonth);
    setLoading(true);
    
    try {
      await fetchAppointmentsForMonth(newMonth);
      setError(null);
    } catch (err) {
      setError("No se pudieron cargar las citas del mes");
    } finally {
      setLoading(false);
    }
  };
  
  // Función para generar los días del mes
  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    
    // Obtener todos los días del mes
    const daysInMonth = eachDayOfInterval({ start, end });
    
    // Determinar el primer día de la semana (0 = domingo, 1 = lunes, etc.)
    const startDay = getDay(start);
    
    // Añadir días del mes anterior para completar la primera semana
    const previousMonthDays = [];
    for (let i = 0; i < startDay; i++) {
      const day = subMonths(end, 1);
      day.setDate(end.getDate() - startDay + i + 1);
      previousMonthDays.push(day);
    }
    
    // Combinar días del mes anterior y actual
    const allDays = [...previousMonthDays, ...daysInMonth];
    
    // Añadir días del mes siguiente para completar la última semana
    const daysNeeded = 42 - allDays.length; // 6 semanas x 7 días = 42
    const nextMonthDays = [];
    for (let i = 0; i < daysNeeded; i++) {
      const day = addMonths(start, 1);
      day.setDate(i + 1);
      nextMonthDays.push(day);
    }
    
    return [...allDays, ...nextMonthDays];
  };
  
  // Función para obtener las citas de un día específico
  const getAppointmentsForDay = (day) => {
    return appointments.filter(appointment => {
      try {
        // Parseamos la fecha directamente desde el string ISO
        const appointmentDate = parseISO(appointment.date);
        // Comparamos solo la fecha (día, mes, año), no la hora
        return isSameDay(appointmentDate, day);
      } catch (e) {
        console.error('Error al comparar fechas:', e, appointment);
        return false;
      }
    });
  };
  
  // Función para obtener el nombre del paciente
  const getPatientName = (patientId) => {
    const patient = patients.find(p => p.patientId === patientId);
    return patient 
      ? `${patient.firstName} ${patient.lastName}`
      : 'Paciente no encontrado';
  };
  
  // Función para formatear la hora
  const formatTime = (dateString) => {
    try {
      // Parseamos la fecha directamente sin ajustes adicionales
      return format(parseISO(dateString), 'HH:mm');
    } catch (e) {
      console.error('Error al formatear hora:', e);
      return '--:--';
    }
  };
  
  // Función para verificar si el paciente tiene número telefónico
  const hasPhoneNumber = (patientId) => {
    const patient = patients.find(p => p.patientId === patientId);
    return patient && patient.phoneNumber && patient.phoneNumber.trim() !== '';
  };
  
  // Manejador para enviar recordatorio
  const handleSendReminder = async (appointmentId) => {
    try {
      setLoading(true);
      await agthealthService.sendAppointmentReminder(appointmentId);
  
      // Actualizar el estado de la cita en la interfaz
      setAppointments(appointments.map(a => 
        a.appointmentId === appointmentId ? { ...a, reminderSent: true } : a
      ));
  
      // Si hay una cita seleccionada, actualizarla también
      if (selectedAppointment && selectedAppointment.appointmentId === appointmentId) {
        setSelectedAppointment({ ...selectedAppointment, reminderSent: true });
      }
  
      setError(null);
      // Mostrar mensaje de éxito
      setSuccessMessage('Recordatorio enviado con éxito');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error al enviar recordatorio:", err);
      setError("No se pudo enviar el recordatorio. Verifique que el paciente tenga un número telefónico válido.");
    } finally {
      setLoading(false);
    }
  };
  
  // Manejador para activar/desactivar recordatorios
  const handleToggleReminders = async (appointmentId, enable) => {
    try {
      setLoading(true);
      await agthealthService.toggleAppointmentReminders(appointmentId, enable);
  
      // Actualizar el estado de la cita en la interfaz
      setAppointments(appointments.map(a => 
        a.appointmentId === appointmentId ? { ...a, enableReminders: enable } : a
      ));
  
      // Si hay una cita seleccionada, actualizarla también
      if (selectedAppointment && selectedAppointment.appointmentId === appointmentId) {
        setSelectedAppointment({ ...selectedAppointment, enableReminders: enable });
      }
  
      setError(null);
      // Mostrar mensaje de éxito
      setSuccessMessage(`Recordatorios ${enable ? 'activados' : 'desactivados'} con éxito`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error al cambiar estado de recordatorios:", err);
      setError("No se pudo cambiar la configuración de recordatorios");
    } finally {
      setLoading(false);
    }
  };
  
  // Manejadores de eventos
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleLogout = async () => {
    try {
      await agthealthService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };
  
  const handleSettings = () => {
    navigate('/settings');
  };
  
  const handleNewPatient = () => {
    setPatientModal({
      open: true,
      patientId: null
    });
  };
  
  const handleClosePatientModal = () => {
    setPatientModal({
      ...patientModal,
      open: false
    });
  };
  
  const handleDateClick = (day) => {
    setSelectedDate(day);
  };
  
  // Manejador para abrir el diálogo de nueva cita
  const handleNewAppointment = () => {
    setSelectedAppointment(null);
    setFormData({
      patientId: '',
      title: 'Consulta médica',
      date: format(selectedDate, "yyyy-MM-dd'T'HH:mm"),
      duration: 30,
      type: 'in-person',
      status: 'scheduled',
      location: '',
      notes: '',
      reason: ''
    });
    setDialogOpen(true);
  };
  
  // Manejador para el menú contextual de citas
  const handleAppointmentMenuClick = (event, appointment) => {
    event.stopPropagation();
    setAppointmentMenuAnchor(event.currentTarget);
    setMenuAppointment(appointment);
  };
  
  const handleAppointmentMenuClose = () => {
    setAppointmentMenuAnchor(null);
    setMenuAppointment(null);
  };
  
  // Manejador para editar una cita
  const handleEditAppointment = (appointment) => {
    handleAppointmentMenuClose();
    setSelectedAppointment(appointment);
    setFormData({
      patientId: appointment.patientId,
      title: appointment.title || 'Consulta médica',
      date: appointment.date ? format(parseISO(appointment.date), "yyyy-MM-dd'T'HH:mm") : '',
      duration: appointment.duration || 30,
      type: appointment.type || 'in-person',
      status: appointment.status || 'scheduled',
      location: appointment.location || '',
      notes: appointment.notes || '',
      reason: appointment.reason || ''
    });
    setDialogOpen(true);
  };
  
  // Manejador para abrir el diálogo de confirmación de eliminación
  const handleDeleteConfirmation = (appointment) => {
    handleAppointmentMenuClose();
    setAppointmentToDelete(appointment);
    setCancellationReason(''); // Resetear el motivo
    setDeleteDialogOpen(true);
  };
  
  // Manejador para eliminar una cita
  const handleDeleteAppointment = async () => {
    if (!appointmentToDelete) return;
    
    try {
      setLoading(true);
      
      // Primero, actualizar el estado a cancelado con el motivo
      await agthealthService.updateAppointment(appointmentToDelete.appointmentId, {
        status: 'cancelled',
        cancellationReason: cancellationReason || 'Solicitud del profesional'
      });
      
      // Luego eliminar la cita
      await agthealthService.deleteAppointment(appointmentToDelete.appointmentId);
      
      // Actualizar la lista de citas
      setAppointments(appointments.filter(a => a.appointmentId !== appointmentToDelete.appointmentId));
      
      setDeleteDialogOpen(false);
      setAppointmentToDelete(null);
      setCancellationReason('');
      setError(null);
      
      // Mostrar mensaje de éxito
      setSuccessMessage('Cita cancelada y notificación enviada al paciente');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error al eliminar cita:", error);
      setError("No se pudo eliminar la cita: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Manejador para cambiar los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Manejador para guardar una cita
  const handleSaveAppointment = async () => {
    try {
      setLoading(true);
  
      // Validar campos obligatorios
      if (!formData.patientId || !formData.date) {
        setError("Los campos de paciente y fecha son obligatorios");
        setLoading(false);
        return;
      }
  
      // Incluir la zona horaria local al enviar la fecha al backend
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const appointmentData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        timezone: timezone // Añadir la zona horaria local
      };
  
      let savedAppointment;
  
      if (selectedAppointment) {
        // Actualizar cita existente
        const response = await agthealthService.updateAppointment(
          selectedAppointment.appointmentId,
          appointmentData
        );
        savedAppointment = response.data;
  
        // Actualizar la lista de citas
        setAppointments(appointments.map(a => 
          a.appointmentId === selectedAppointment.appointmentId ? savedAppointment : a
        ));
        
        // Mostrar mensaje de éxito
        setSuccessMessage('Cita actualizada correctamente');
      } else {
        // Crear nueva cita
        const response = await agthealthService.createAppointment(appointmentData);
        savedAppointment = response.data;
  
        // Añadir la nueva cita a la lista
        setAppointments([...appointments, savedAppointment]);
        
        // Mostrar mensaje de éxito
        setSuccessMessage('Cita creada correctamente');
      }
  
      setDialogOpen(false);
      setError(null);
      
      // Ocultar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error al guardar cita:", err);
      setError(err.response?.data?.message || "No se pudo guardar la cita");
    } finally {
      setLoading(false);
    }
  };
  
  // Obtener los días del mes actual
  const daysInMonth = getDaysInMonth();
  
  // Obtener las citas del día seleccionado
  const appointmentsForSelectedDate = getAppointmentsForDay(selectedDate);
  
  // Ordenar las citas por hora
  const sortedAppointments = [...appointmentsForSelectedDate].sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );
  
  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar compartido */}
      <Sidebar
        open={drawerOpen}
        isMobile={isMobile}
        onClose={handleDrawerToggle}
        onNewPatient={handleNewPatient}
        onLogout={handleLogout}
        activeSection="appointments"
      />
      
      {/* Main Content */}
      <Main open={drawerOpen} isMobile={isMobile}>
        <DashboardHeader 
          title="Agenda de Citas"
          user={user}
          isMobile={isMobile}
          onMenuClick={handleDrawerToggle}
          onLogout={handleLogout}
          onSettings={handleSettings}
        />
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        
        <Grid container spacing={3}>
          {/* Calendario */}
          <Grid item xs={12} md={8}>
            <CalendarContainer>
              <CalendarHeader>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={() => changeMonth('prev')}>
                    <ChevronLeft />
                  </IconButton>
                  <Typography variant="h6" sx={{ fontWeight: 600, mx: 2 }}>
                    {format(currentMonth, 'MMMM yyyy', { locale: es })}
                  </Typography>
                  <IconButton onClick={() => changeMonth('next')}>
                    <ChevronRight />
                  </IconButton>
                </Box>
                <ActionButton 
                  startIcon={<Plus size={16} />}
                  onClick={handleNewAppointment}
                >
                  Nueva Cita
                </ActionButton>
              </CalendarHeader>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <CircularProgress sx={{ color: HEALTH_GREEN }} />
                </Box>
              ) : (
                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                  <CalendarGrid>
                    {/* Encabezados de los días de la semana */}
                    {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day, index) => (
                      <DayHeader key={index}>
                        {day}
                      </DayHeader>
                    ))}
                    
                    {/* Días del mes */}
                    {daysInMonth.map((day, index) => {
                      const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                      const dayAppointments = getAppointmentsForDay(day);
                      const isSelected = isSameDay(day, selectedDate);
                      const isTodayDate = isToday(day);
                      
                      return (
                        <DayCell 
                          key={index} 
                          istoday={isTodayDate.toString()}
                          isselected={isSelected.toString()}
                          isoutofmonth={(!isCurrentMonth).toString()}
                          onClick={() => handleDateClick(day)}
                        >
                          <DayNumber istoday={isTodayDate.toString()}>
                            {day.getDate()}
                          </DayNumber>
                          
                          {/* Mostrar hasta 3 citas por día */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {dayAppointments.slice(0, 3).map((appointment) => (
                              <AppointmentChip 
                                key={appointment.appointmentId}
                                status={appointment.status}
                                onClick={(e) => handleAppointmentMenuClick(e, appointment)}
                              >
                                {formatTime(appointment.date)}
                              </AppointmentChip>
                            ))}
                          </Box>
                          
                          {/* Indicador de más citas */}
                          {dayAppointments.length > 3 && (
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                              +{dayAppointments.length - 3} más
                            </Typography>
                          )}
                        </DayCell>
                      );
                    })}
                  </CalendarGrid>
                </Box>
              )}
            </CalendarContainer>
          </Grid>
          
          {/* Detalle del día seleccionado */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ 
              borderRadius: 4, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {format(selectedDate, "EEEE, d 'de' MMMM", { locale: es })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sortedAppointments.length} citas programadas
                </Typography>
              </Box>
              
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Citas para {format(selectedDate, "EEEE, d 'de' MMMM", { locale: es })}
                </Typography>
                
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  {sortedAppointments.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Calendar size={40} strokeWidth={1} style={{ marginBottom: 16, opacity: 0.5 }} />
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        No hay citas para este día
                      </Typography>
                      <Button 
                        variant="contained"
                        onClick={handleNewAppointment}
                        sx={{ 
                          borderRadius: 12,
                          backgroundColor: HEALTH_GREEN,
                          '&:hover': { backgroundColor: '#3d8b40' },
                          textTransform: 'none'
                        }}
                      >
                        Programar Cita
                      </Button>
                    </Box>
                  ) : (
                    sortedAppointments.map((appointment) => (
                      <Paper
                        key={appointment.appointmentId}
                        elevation={0}
                        sx={{ 
                          p: 2, 
                          mb: 2, 
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {appointment.title || 'Consulta médica'}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleAppointmentMenuClick(e, appointment)}
                          >
                            <MoreVertical size={16} />
                          </IconButton>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Clock size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                          <Typography variant="body2">
                            {formatTime(appointment.date)} ({appointment.duration} min)
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <User size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                          <Typography variant="body2">
                            {getPatientName(appointment.patientId)}
                          </Typography>
                        </Box>
                        
                        {appointment.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {appointment.type === 'video' ? (
                              <Video size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                            ) : appointment.type === 'phone' ? (
                              <Phone size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                            ) : (
                              <MapPin size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                            )}
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {appointment.location}
                            </Typography>
                          </Box>
                        )}
                        
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip
                            size="small"
                            label={
                              appointment.status === 'scheduled' ? 'Programada' :
                              appointment.status === 'completed' ? 'Completada' :
                              appointment.status === 'cancelled' ? 'Cancelada' : 'No asistió'
                            }
                            sx={{ 
                              borderRadius: 1,
                              backgroundColor: 
                                appointment.status === 'scheduled' ? 'rgba(33, 150, 243, 0.1)' :
                                appointment.status === 'completed' ? 'rgba(76, 175, 80, 0.1)' :
                                appointment.status === 'cancelled' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                              color: 
                                appointment.status === 'scheduled' ? '#2196f3' :
                                appointment.status === 'completed' ? HEALTH_GREEN :
                                appointment.status === 'cancelled' ? '#f44336' : '#ff9800'
                            }}
                            icon={
                              appointment.status === 'scheduled' ? <Clock size={12} /> :
                              appointment.status === 'completed' ? <CheckCircle size={12} /> :
                              appointment.status === 'cancelled' ? <XCircle size={12} /> : <AlertCircle size={12} />
                            }
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Eye size={14} />}
                            onClick={() => setSelectedAppointment(appointment)}
                            sx={{ 
                              textTransform: 'none',
                              color: HEALTH_GREEN,
                              borderColor: HEALTH_GREEN,
                              borderRadius: 8,
                              '&:hover': {
                                borderColor: HEALTH_GREEN,
                                backgroundColor: 'rgba(76, 175, 80, 0.1)'
                              }
                            }}
                          >
                            Detalles
                          </Button>
                        </Box>
                      </Paper>
                    ))
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Main>
      
      {/* Modal de paciente */}
      <PatientModal
        open={patientModal.open}
        onClose={handleClosePatientModal}
        patientId={patientModal.patientId}
      />
      
      {/* Modal de detalles de cita */}
      <Modal
        open={Boolean(selectedAppointment)}
        onClose={() => setSelectedAppointment(null)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backdropFilter: 'blur(3px)', backgroundColor: 'transparent' }
        }}
      >
        <DetailsModal>
          {selectedAppointment && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedAppointment.title || 'Consulta médica'}
                </Typography>
                <IconButton onClick={() => setSelectedAppointment(null)}>
                  <XCircle size={20} />
                </IconButton>
              </Box>
              
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Información de la Cita
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Fecha y Hora
                  </Typography>
                  <Typography variant="body2">
                    {format(parseISO(selectedAppointment.date), "dd MMM yyyy, HH:mm", { locale: es })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Duración
                  </Typography>
                  <Typography variant="body2">
                    {selectedAppointment.duration} minutos
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Tipo
                  </Typography>
                  <Typography variant="body2">
                    {selectedAppointment.type === 'in-person' ? 'Presencial' : 
                     selectedAppointment.type === 'video' ? 'Videollamada' : 
                     selectedAppointment.type === 'phone' ? 'Telefónica' : 'No especificado'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Estado
                  </Typography>
                  <Typography variant="body2">
                    {selectedAppointment.status === 'scheduled' ? 'Programada' :
                     selectedAppointment.status === 'completed' ? 'Completada' :
                     selectedAppointment.status === 'cancelled' ? 'Cancelada' : 'No asistió'}
                  </Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Paciente
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {getPatientName(selectedAppointment.patientId)}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Eye size={14} />}
                  onClick={() => navigate(`/patients/${selectedAppointment.patientId}`)}
                  sx={{ 
                    mt: 1, 
                    textTransform: 'none',
                    color: HEALTH_GREEN
                  }}
                >
                  Ver ficha del paciente
                </Button>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Recordatorios
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedAppointment.enableReminders !== false}
                      onChange={(e) => handleToggleReminders(selectedAppointment.appointmentId, e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Activar recordatorios automáticos"
                />
                
                <Button
                  variant="outlined"
                  startIcon={<Send size={16} />}
                  onClick={() => handleSendReminder(selectedAppointment.appointmentId)}
                  disabled={!hasPhoneNumber(selectedAppointment.patientId)}
                  sx={{ 
                    borderRadius: 2,
                    borderColor: HEALTH_GREEN,
                    color: HEALTH_GREEN,
                    '&:hover': { borderColor: HEALTH_GREEN, backgroundColor: 'rgba(76, 175, 80, 0.1)' },
                    textTransform: 'none'
                  }}
                >
                  Enviar Recordatorio Ahora
                </Button>
              </Box>
              
              {!hasPhoneNumber(selectedAppointment.patientId) && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  El paciente no tiene un número de teléfono registrado para enviar recordatorios por WhatsApp.
                </Alert>
              )}
              
              {selectedAppointment.reminderSent && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Se ha enviado un recordatorio para esta cita.
                </Alert>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              {selectedAppointment.location && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Ubicación
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedAppointment.location}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                </>
              )}
              
              {selectedAppointment.reason && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Motivo de la Consulta
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedAppointment.reason}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                </>
              )}
              
              {selectedAppointment.notes && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Notas
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedAppointment.notes}
                  </Typography>
                </>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<Edit2 size={16} />}
                  onClick={() => {
                    handleEditAppointment(selectedAppointment);
                    setSelectedAppointment(null);
                  }}
                  sx={{ 
                    borderRadius: 2,
                    borderColor: HEALTH_GREEN,
                    color: HEALTH_GREEN,
                    '&:hover': { borderColor: HEALTH_GREEN, backgroundColor: 'rgba(76, 175, 80, 0.1)' },
                    textTransform: 'none'
                  }}
                >
                  Editar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Trash2 size={16} />}
                  onClick={() => {
                    handleDeleteConfirmation(selectedAppointment);
                    setSelectedAppointment(null);
                  }}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none'
                  }}
                >
                  Eliminar
                </Button>
              </Box>
            </>
          )}
        </DetailsModal>
      </Modal>
      
      {/* Diálogo para crear/editar citas */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {selectedAppointment ? 'Editar Cita' : 'Nueva Cita'}
        </DialogTitle>
        <DialogContent sx={{ pb: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="patient-label">Paciente *</InputLabel>
                <Select
                  labelId="patient-label"
                  name="patientId"
                  value={formData.patientId}
                  onChange={handleInputChange}
                  label="Paciente *"
                  disabled={loading}
                >
                  {patients.map(patient => (
                    <MenuItem key={patient.patientId} value={patient.patientId}>
                      {patient.firstName} {patient.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Título"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Fecha y Hora *"
                name="date"
                type="datetime-local"
                value={formData.date}
                onChange={handleInputChange}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="type-label">Tipo de Cita</InputLabel>
                <Select
                  labelId="type-label"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  label="Tipo de Cita"
                  disabled={loading}
                >
                  <MenuItem value="in-person">Presencial</MenuItem>
                  <MenuItem value="video">Videollamada</MenuItem>
                  <MenuItem value="phone">Telefónica</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Duración (minutos)"
                name="duration"
                type="number"
                value={formData.duration}
                onChange={handleInputChange}
                disabled={loading}
                InputProps={{ inputProps: { min: 5, step: 5 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Estado</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Estado"
                  disabled={loading}
                >
                  <MenuItem value="scheduled">Programada</MenuItem>
                  <MenuItem value="completed">Completada</MenuItem>
                  <MenuItem value="cancelled">Cancelada</MenuItem>
                  <MenuItem value="no-show">No asistió</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Ubicación"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                disabled={loading}
                placeholder={formData.type === 'in-person' ? 'Dirección o sala de consulta' : 'URL o número telefónico'}
              />
            </Grid>
            
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Motivo de la consulta"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                disabled={loading}
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Notas adicionales"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={loading}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveAppointment}
            variant="contained"
            disabled={loading}
            sx={{ 
              borderRadius: 2,
              bgcolor: HEALTH_GREEN,
              '&:hover': { bgcolor: '#3d8b40' }
            }}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo de confirmación de eliminación */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1
          }
        }}
      >
        <DialogTitle>Confirmar cancelación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas cancelar esta cita? Esta acción no se puede deshacer.
          </Typography>
          
          <TextField
            fullWidth
            label="Motivo de la cancelación"
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteAppointment}
            variant="contained"
            color="error"
            disabled={loading || !cancellationReason.trim()}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Eliminando...' : 'Cancelar cita'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Menú contextual para citas */}
      <Menu
        anchorEl={appointmentMenuAnchor}
        open={Boolean(appointmentMenuAnchor)}
        onClose={handleAppointmentMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        <MenuItem onClick={() => handleEditAppointment(menuAppointment)}>
          <ListItemIcon>
            <Edit2 size={16} />
          </ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteConfirmation(menuAppointment)}>
          <ListItemIcon>
            <Trash2 size={16} />
          </ListItemIcon>
          <ListItemText>Eliminar</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AppointmentsPage;